import { useState, useRef } from 'react';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

import JxSelect from '../../../../../JxControls/JxSelect/JxSelect';
import JxIconButton from '../../../../../JxControls/JxButtons/JxIconButton';
import JxSearchEdit from '../../../../../JxControls/JxEdits/JxSearchEdit';
import FilterEdit from './FilterEdit';
import floatingPanelEngine from '../../Core/FloatingPanelEngine';
import useSearchResults from './useSearchResults';
import SearchDrawerPager from './SearchDrawerPager';
import SearchList from './SearchList';

import './SearchDrawer.css';

/**
 * SearchDrawer — left pane of FloatingContentPanel.
 *
 * Pure painter — all state via useSearchResults, all actions via provider from floatingPanelEngine.
 * Width + splitter are owned by FloatingContentPanelBody — passed in as props so spacer and
 * drawer stay in sync without extra state here.
 *
 * @param {string}   props.panelId
 * @param {string}   props.initialQuery
 * @param {string}   props.drawerClass
 * @param {number}   props.drawerWidth
 * @param {boolean}  props.pinned
 * @param {function} props.onTogglePin
 * @param {function} props.onSplitterMouseDown
 */
export default function SearchDrawer({ panelId, initialQuery = '', drawerClass, drawerWidth, pinned, onTogglePin, onSplitterMouseDown }) {
    const { results, total, query, topic, loading, topicOptions, selectedEntry } = useSearchResults(panelId);
    const provider = floatingPanelEngine.getProvider(panelId);

    const [filter, setFilter] = useState('');
    const listRef = useRef(null);
    const searchRef = useRef(null);
    const handleArrowDown = () => listRef.current?.focusFirst();
    const handleBackToSearch = () => document.getElementById(`sd-search-${panelId}`)?.focus();
    const filteredResults = filter.trim() ? results.filter((r) => r.title?.toLowerCase().includes(filter.toLowerCase())) : results;

    const pinIcon = pinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />;

    function handleTopicChange(value) {
        provider?.setTopic(value);
    }
    function handleSearch(value) {
        provider?.provide(value);
    }

    return (
        <div className={drawerClass ?? 'sd-root sd-root--open'} style={{ width: drawerWidth, minWidth: drawerWidth }}>
            {/* Topic filter + pin button */}
            <div id="sd-filter-row" className="sd-filter-row">
                <JxIconButton icon={pinIcon} onClick={onTogglePin} title={pinned ? 'Unpin drawer' : 'Pin drawer'} active={pinned} />
                <JxSelect id={`sd-topic-select-${panelId}`} value={topic} onChange={handleTopicChange} options={topicOptions} />
            </div>

            {/* Search input */}
            <div id="sd-input-row" className="sd-input-row">
                <JxSearchEdit
                    id={`sd-search-${panelId}`}
                    defaultValue={initialQuery}
                    onSearch={handleSearch}
                    onArrowDown={handleArrowDown}
                    autoFocus
                />
            </div>

            {/* Client-side filter */}
            <div id="sd-filter-edit-row" className="sd-filter-edit-row">
                <FilterEdit id={`sd-filter-${panelId}`} value={filter} onChange={setFilter} />
            </div>

            {/* Pager — only rendered when totalPages > 1 */}
            <SearchDrawerPager panelId={panelId} total={total} query={query} />

            {/* Result list */}
            <div id="sd-results" className="sd-results">
                <SearchList
                    ref={listRef}
                    results={filteredResults}
                    query={query}
                    loading={loading}
                    selectedEntry={selectedEntry}
                    onRowClick={(item) => provider?.selectEntry(item)}
                    onBackToSearch={handleBackToSearch}
                />
            </div>

            {/* Splitter — right edge, drag to resize */}
            <div className="sd-splitter" onMouseDown={onSplitterMouseDown} />
        </div>
    );
}
